import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Compass } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';

export const Home = () => {
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Check for reduced motion preference on mount
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Set page title and meta description
    document.title = 'Wild Thyme - Nourishing Retreats for Women in the UK';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Thoughtfully designed retreats for women seeking rest, reconnection, and personal growth. Home-cooked food, nature, reflection, and supportive company in beautiful UK locations.';
  }, []);

  // Handle video ready state and autoplay
  const handleVideoCanPlay = async () => {
    if (!videoRef.current) return;
    
    try {
      await videoRef.current.play();
      setVideoReady(true);
    } catch (error) {
      console.log('Video autoplay failed, showing fallback');
      setVideoFailed(true);
    }
  };

  // Determine what to show
  // Video layer: always render if not reduced motion, but hidden until ready
  const shouldRenderVideo = !prefersReducedMotion;
  // Poster visibility: show for reduced-motion, or as fallback when video fails
  const posterOpacity = prefersReducedMotion || videoFailed ? 1 : 0;
  // Video visibility: only show when ready and playing
  const videoOpacity = videoReady ? 1 : 0;

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0 bg-[#2C332B]">
          {/* Layer 1: Poster/Fallback Image - hidden initially, shown for reduced-motion or video failure */}
          <img
            src="https://customer-assets.emergentagent.com/job_thyme-adventures/artifacts/w8z2jgg5_istockphoto-2203656488-2048x2048.jpg"
            alt="Women enjoying outdoor activities together"
            className="absolute inset-0 w-full h-full object-cover hero-fallback"
            style={{
              opacity: posterOpacity,
              transition: 'opacity 300ms ease-in-out',
              zIndex: 1,
            }}
          />
          
          {/* Layer 2: Video - renders but hidden until ready, then cross-fades in */}
          {shouldRenderVideo && (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onCanPlay={handleVideoCanPlay}
              onError={() => setVideoFailed(true)}
              className="absolute inset-0 w-full h-full object-cover hero-video"
              style={{ 
                filter: 'brightness(0.95)',
                opacity: videoOpacity,
                transition: 'opacity 300ms ease-in-out',
                zIndex: 2,
              }}
            >
              <source
                src="https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/y6x1rgqe_iStock-1283741951.mp4?v=2"
                type="video/mp4"
              />
            </video>
          )}
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-[#F9F8F6]" style={{ zIndex: 3 }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-white/90 mb-6" data-testid="hero-tagline">
              Adventure & Wellbeing Through Time Out
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.1] tracking-tight text-white mb-8" data-testid="hero-title">
              Escape. Explore.
              <br />
              <span className="text-[#D4A373]">Exhale.</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 max-w-2xl mx-auto mb-12" data-testid="hero-description">
              Carefully curated experiences designed to allow you to step beyond routine and embrace something more.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/book"
                className="inline-flex items-center gap-2 px-8 py-6 text-lg font-medium bg-[#3A4A3F] text-white rounded-full hover:bg-[#2C332B] transition-all duration-300 hover:scale-[1.02]"
                data-testid="hero-cta-book"
              >
                View Upcoming Retreats
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/experiences"
                className="inline-flex items-center gap-2 px-8 py-6 text-lg font-medium border border-white/40 text-white bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-300"
                data-testid="hero-cta-explore"
              >
                Learn About Our Approach
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Experiences */}
      <section className="py-16 md:py-20 bg-[#E8E4DD]" data-testid="upcoming-experiences-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              What's Coming Up
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B]">
              Upcoming Experiences
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Wild Swimming',
                date: '19 September 2026',
                description: 'A guided introduction to wild swimming and hiking in the stunning Pennines. Join us for a day of adventure, fun, connection and expanding your comfort zone.',
                image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/8i5xssdc_Wild%20Swim.jpeg',
                url: 'https://147723757.hs-sites-eu1.com/wild-swim-sept26',
              },
              {
                title: 'Samhain Fire Walk',
                date: '29 October 2026',
                description: 'Step towards the glowing embers and leave behind what no longer serves you. An unforgettable evening of fire, connection and discovering what you\u2019re truly capable of.',
                image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/6ja6780e_FireWalk.png',
                url: 'https://147723757.hs-sites-eu1.com/wild-women-firewalk',
              },
              {
                title: 'Wild Women',
                date: '20\u201322 November 2026',
                description: 'Reconnect with your inner wisdom, awaken your feminine power and remember the woman you came here to be.',
                image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/9thxhl7a_2Wild%20Women.jpeg',
                url: 'https://147723757.hs-sites-eu1.com/rise-reclaim',
              },
            ].map((experience, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden shadow-lg"
                data-testid={`upcoming-${idx}`}
              >
                <div className="relative h-64">
                  <img
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <p className="text-sm uppercase tracking-widest font-bold text-[#D4A373] mb-2">
                    {experience.date}
                  </p>
                  <h3 className="font-serif text-2xl font-semibold text-[#2C332B] mb-3">
                    {experience.title}
                  </h3>
                  <p className="text-[#6C757D] leading-relaxed mb-6">
                    {experience.description}
                  </p>
                  <a
                    href={experience.url}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4A3F] text-white rounded-full font-medium hover:bg-[#2C332B] transition-all duration-300 hover:gap-3"
                    data-testid={`view-details-${idx}`}
                  >
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 md:py-32 bg-[#F9F8F6]" data-testid="value-prop-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Why Wild Thyme
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B] mb-6">
              Time Out That Truly Matters
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D] max-w-3xl mx-auto">
              We create carefully guided experiences that help you step away from everyday pressures—balancing energising outdoor movement with restorative indoor moments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'In Safe Hands',
                description:
                  'Experienced, qualified guides who prioritize your safety and comfort at every step.',
              },
              {
                icon: Heart,
                title: 'Human & Personal',
                description:
                  'Small groups and flexible approaches mean every experience feels personal and authentic.',
              },
              {
                icon: Compass,
                title: 'Adventure with Purpose',
                description:
                  'Every experience balances active outdoor moments with cosy indoor reflection—designed for your wellbeing, not just activity.',
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white/50 backdrop-blur-sm border border-white/60 shadow-sm hover:shadow-md transition-all duration-500 rounded-3xl p-8"
                data-testid={`value-card-${idx}`}
              >
                <div className="w-14 h-14 bg-[#3A4A3F] rounded-2xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#2C332B] mb-4">
                  {item.title}
                </h3>
                <p className="text-[#6C757D] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Words from Our Guests
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B]">
              What Women Are Saying
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  'I arrived feeling nervous and left feeling grounded, confident, and supported. The whole experience felt thoughtful and safe.',
                author: 'Brenda',
              },
              {
                quote:
                  'The food, the conversations, and the space to just be myself were exactly what I didn\'t know I needed.',
                author: 'Clare',
              },
              {
                quote:
                  'Beautifully organised, deeply nourishing, and genuinely welcoming. I\'d recommend this to any woman needing time to reset.',
                author: 'Naomi',
              },
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="bg-[#F9F8F6] rounded-3xl p-8 flex flex-col h-full"
                data-testid={`testimonial-${idx}`}
              >
                <div className="mb-6">
                  <svg
                    className="w-10 h-10 text-[#D4A373] opacity-50"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="text-lg leading-relaxed text-[#2C332B] mb-6 font-serif italic flex-grow">
                  "{testimonial.quote}"
                </p>
                <p className="text-sm font-medium text-[#6C757D] mt-auto">— {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Types */}
      <section className="py-24 md:py-32 bg-[#E8E4DD]" data-testid="experience-types-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Our Experiences
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B] mb-6">
              Choose the Experience That Fits You
            </h2>
            <div className="text-lg leading-relaxed text-[#6C757D] max-w-3xl mx-auto space-y-4">
              <p>
                We know that everyone has different amounts of time, budgets and commitments, which is why we offer a range of experiences to make it easier to reconnect with nature in a way that works for you. Whether you're ready to immerse yourself in a full weekend retreat or simply looking for a restorative day outdoors, there's an option to suit you.
              </p>
              <p>
                Please note that not every retreat offers all of these experiences, so be sure to check the individual event details before booking.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: 'The Day Escape',
                price: 'Prices from \u00a345 per person',
                description: "A wonderful introduction to our retreats and activities. Perfect if you're short on time. Spend the day exploring beautiful landscapes, enjoying guided activities and sharing delicious homemade food before returning home refreshed, restored and inspired.",
                duration: 'One day',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/vpuormpe_FB_IMG_1745789334766.jpg',
              },
              {
                title: 'The Overnight Experience',
                price: 'From \u00a3175 per person',
                description: "Ideal if you can't make a full weekend but still want to experience the magic of an overnight escape. Join us on Saturday morning for a full day of adventure, shared meals and connection before settling in for the night and enjoying a relaxed Sunday morning before heading home.",
                duration: 'Saturday morning to Sunday lunchtime',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/fef339tf_PXL_20250817_181526137.jpg',
              },
              {
                title: 'The Complete Retreat',
                price: 'From \u00a3300 per person',
                description: 'Our signature weekend experience gives you time to fully unwind, slow down and immerse yourself in the rhythm of the outdoors. Guided wild swims, walks, nourishing food, accommodation and plenty of time to relax create the perfect opportunity to reset and reconnect.',
                duration: 'Friday lunchtime to Sunday lunchtime',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/o7uorl3a_walk%20to%20snails.jpg',
              },
            ].map((type, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden shadow-lg"
                data-testid={`experience-type-${idx}`}
              >
                <div className="relative h-64">
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl font-semibold text-[#2C332B] mb-2">
                    {type.title}
                  </h3>
                  <p className="text-lg font-bold text-[#D4A373] mb-4">{type.price}</p>
                  <p className="text-[#6C757D] leading-relaxed mb-6">
                    {type.description}
                  </p>
                  <p className="text-sm font-medium text-[#2C332B]">
                    Typical duration: <span className="text-[#6C757D]">{type.duration}</span>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 px-8 py-6 text-lg font-medium border border-[#3A4A3F]/20 text-[#3A4A3F] rounded-full hover:bg-[#3A4A3F]/5 transition-all duration-300"
              data-testid="book-your-experience"
            >
              Book Your Experience
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 md:py-16 bg-[#E8E4DD]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              You're in Safe Hands
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B] mb-6">
              Experienced, Qualified Guides
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D] max-w-3xl mx-auto">
              All our guides are fully qualified, insured, and passionate about creating
              meaningful experiences in nature. Your safety and wellbeing are our priority.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/vpuormpe_FB_IMG_1745789334766.jpg" 
              alt="Women wild swimming at sunset"
              className="w-full max-w-3xl h-auto rounded-3xl shadow-lg object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#3A4A3F] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-6xl leading-tight mb-8">
              Ready for Your Next Adventure?
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-12">
              Book your experience today and take the first step toward meaningful time
              outdoors.
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 px-8 py-6 text-lg font-medium bg-[#D4A373] text-white rounded-full hover:bg-[#C69363] transition-all duration-300 hover:scale-[1.02]"
              data-testid="final-cta-book"
            >
              Book Your Escape
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
